import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { getActivityLog } from "../../api/activityLogApi";
import Loader from "./Loader";

// ---- Full enum map (mirrors backend ActivityType) ----
export const ACTIVITY_TYPE = {
  BusinessCreate: 1,
  BusinessUpdate: 2,
  TerminalCreate: 3,
  TerminalUpdate: 4,
  FeeUpdate: 5,
  WkndSrchUpdate: 6,
  PHSrchUpdate: 7,
  Information: 8,
  Warning: 9,
  Error: 10,
};

// ---- Display labels + colors per type ----
const ACTIVITY_META = {
  [ACTIVITY_TYPE.BusinessCreate]: {
    label: "Business Create",
    color: "success",
  },
  [ACTIVITY_TYPE.BusinessUpdate]: {
    label: "Business Update",
    color: "primary",
  },
  [ACTIVITY_TYPE.TerminalCreate]: {
    label: "Terminal Create",
    color: "success",
  },
  [ACTIVITY_TYPE.TerminalUpdate]: {
    label: "Terminal Update",
    color: "primary",
  },
  [ACTIVITY_TYPE.FeeUpdate]: { label: "Fee Update", color: "info" },
  [ACTIVITY_TYPE.WkndSrchUpdate]: {
    label: "Weekend Surcharge Update",
    color: "info",
  },
  [ACTIVITY_TYPE.PHSrchUpdate]: { label: "PH Surcharge Update", color: "info" },
  [ACTIVITY_TYPE.Information]: { label: "Information", color: "secondary" },
  [ACTIVITY_TYPE.Warning]: { label: "Warning", color: "warning" },
  [ACTIVITY_TYPE.Error]: { label: "Error", color: "danger" },
};

export const BUSINESS_FILTER_TYPES = [
  ACTIVITY_TYPE.BusinessCreate,
  ACTIVITY_TYPE.BusinessUpdate,
];

export const TERMINAL_FILTER_TYPES = [
  ACTIVITY_TYPE.TerminalCreate,
  ACTIVITY_TYPE.TerminalUpdate,
  ACTIVITY_TYPE.FeeUpdate,
  ACTIVITY_TYPE.WkndSrchUpdate,
  ACTIVITY_TYPE.PHSrchUpdate,
  ACTIVITY_TYPE.Information,
  ACTIVITY_TYPE.Warning,
  ACTIVITY_TYPE.Error,
];

const buildOptions = (ids) =>
  ids.map((id) => ({
    value: id,
    label: ACTIVITY_META[id]?.label ?? `Type ${id}`,
  }));

// ---- Normalize a raw API row into the shape the table expects ----
const mapLogRow = (row, idx) => ({
  id: row.id ?? `${row.activityTime}-${idx}`,
  activityType: row.activityType,
  activityTime: row.activityTime,
  message: row.data,
  user: row.activityBy,
});

const formatDateTime = (value) => {
  if (!value) return "—";
  let str = String(value);

  // Treat timezone-less strings as UTC (adjust if your API sends local time)
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(str)) str = `${str}Z`;

  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleString(undefined, {
    // undefined = browser's locale + timezone
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const MessageCell = ({ text }) => {
  if (!text) return <span>—</span>;
  return (
    <>
      {String(text)
        .split("\n")
        .filter((line) => line.trim() !== "")
        .map((line, i) => (
          <div key={i} style={{ whiteSpace: "pre-wrap" }}>
            {line}
          </div>
        ))}
    </>
  );
};

const columns = [
  {
    name: "Date/Time",
    selector: (row) => row.activityTime,
    cell: (row) => <small>{formatDateTime(row.activityTime)}</small>,
    width: "180px",
  },
  {
    name: "Type",
    selector: (row) =>
      ACTIVITY_META[row.activityType]?.label ?? `Type ${row.activityType}`,
    cell: (row) => {
      const meta = ACTIVITY_META[row.activityType] || {
        label: `Type ${row.activityType}`,
        color: "secondary",
      };
      return <span className={`badge bg-${meta.color}`}>{meta.label}</span>;
    },
    width: "200px",
  },
  {
    name: "Message",
    selector: (row) => row.message,
    cell: (row) => (
      <small>
        <MessageCell text={row.message} />
      </small>
    ),
    grow: 2,
    wrap: true,
  },
  {
    name: "User",
    selector: (row) => row.user,
    cell: (row) => <small>{row.user || "—"}</small>,
    width: "140px",
  },
];

const customStyles = {
  headRow: { style: { backgroundColor: "#f8f9fa", fontWeight: 600 } },
  cells: { style: { paddingTop: "8px", paddingBottom: "8px" } },
};

const ActivityLogModal = ({
  show,
  onClose,
  entityType = "Business",
  entityId,
  title,
  filterTypes = [],
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState(() =>
    buildOptions(filterTypes),
  );

  // Cache total count across pages. Only refreshed on page 1.
  const totalCountRef = useRef(0);

  // Track open/close transitions so reset only runs once per open.
  const wasOpenRef = useRef(false);

  // De-dupe fetch: remember the last (query + page + pageSize) we fetched.
  const lastFetchedKeyRef = useRef(null);

  // Stable request token to ignore stale responses.
  const requestIdRef = useRef(0);

  // All available options for the dropdown
  const options = useMemo(() => buildOptions(filterTypes), [filterTypes]);

  // Stable string of selected type ids (used inside queryKey)
  const selectedTypeIds = useMemo(
    () =>
      selectedTypes
        .map((t) => t.value)
        .sort((a, b) => a - b)
        .join(","),
    [selectedTypes],
  );

  const queryKey = `${entityType}|${entityId}|${selectedTypeIds}`;

  const fetchLogs = useCallback(
    async ({ force = false } = {}) => {
      if (!show || !entityId) return;

      const fetchKey = `${queryKey}|${page}|${pageSize}`;

      // Prevent duplicate consecutive fetches unless explicitly forced.
      if (!force && lastFetchedKeyRef.current === fetchKey) return;
      lastFetchedKeyRef.current = fetchKey;

      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const typesToSend =
          selectedTypes.length > 0
            ? selectedTypes.map((t) => t.value)
            : filterTypes; // fallback: never send an empty array

        const response = await getActivityLog({
          entityType,
          entityId,
          activityTypes: typesToSend,
          page,
          pageSize,
        });

        // Ignore stale responses (e.g., user changed filters mid-flight).
        if (requestId !== requestIdRef.current) return;

        const items = response?.items || response?.data || [];
        setLogs(Array.isArray(items) ? items.map(mapLogRow) : []);

        // ---- Total count handling ----
        if (page === 1) {
          if (response?.totalCount != null) {
            totalCountRef.current = response.totalCount;
            setTotalCount(response.totalCount);
          } else if (response?.totalPages != null) {
            const derived = response.totalPages * pageSize;
            totalCountRef.current = derived;
            setTotalCount(derived);
          } else {
            totalCountRef.current = items.length;
            setTotalCount(items.length);
          }
        } else {
          setTotalCount(totalCountRef.current);
        }
      } catch (err) {
        if (requestId === requestIdRef.current) {
          setLogs([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      show,
      entityId,
      entityType,
      selectedTypes,
      filterTypes,
      page,
      pageSize,
      queryKey,
    ],
  );

  // ---- Reset on open, clear on close ----
  useEffect(() => {
    if (!show) {
      wasOpenRef.current = false;
      lastFetchedKeyRef.current = null;
      requestIdRef.current = 0;
      return;
    }

    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      lastFetchedKeyRef.current = null;
      setPage(1);
      setSelectedTypes(buildOptions(filterTypes));
      setTotalCount(0);
      totalCountRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, filterTypes]);

  // ---- Single fetch effect ----
  useEffect(() => {
    if (!show || !entityId) return;
    if (!wasOpenRef.current) return; // wait until reset has run
    fetchLogs();
  }, [show, entityId, fetchLogs]);

  // Close on Escape
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, loading, onClose]);

  if (!show) return null;

  return (
    <div
      className="modal d-block"
      style={{ zIndex: 1060, backgroundColor: "rgba(0,0,0,0.5)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between">
            <h5 className="modal-title">
              <i className="bi bi-clock-history me-2"></i>
              {title || "Activity Log"}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => fetchLogs({ force: true })}
                disabled={loading}
                title="Refresh"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              />
            </div>
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Filter by Activity Type</label>
              <Select
                isMulti
                options={options}
                value={selectedTypes}
                onChange={(selected) => {
                  setSelectedTypes(selected || []);
                  // Reset pagination whenever filters change
                  setPage(1);
                  totalCountRef.current = 0;
                  setTotalCount(0);
                }}
                placeholder="All activity types"
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={loading}
              />
            </div>

            <DataTable
              columns={columns}
              data={logs}
              progressPending={loading}
              className="business-table"
              progressComponent={
                <Loader message="Loading activity log..." size="medium" />
              }
              noDataComponent={
                <div className="text-center py-4">
                  <i
                    className="bi bi-journal-text display-4"
                    style={{ color: "var(--color-peach-glow)" }}
                  ></i>
                  <p className="text-theme-muted mt-2">No activity found</p>
                </div>
              }
              customStyles={customStyles}
              pagination
              paginationServer
              paginationTotalRows={totalCount}
              paginationDefaultPage={page}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              onChangePage={(p) => setPage(p)}
              onChangeRowsPerPage={(newPerPage, newPage) => {
                setPageSize(newPerPage);
                setPage(newPage);
              }}
              persistTableHead
              highlightOnHover
              responsive
              dense
            />
          </div>

          <div className="modal-footer"></div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogModal;
